import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AI_LISTENING_BRAND_NEW_ROUTE,
  AI_LISTENING_BRAND_PROFILE_ROUTE,
  AI_LISTENING_BRAND_ROUTE,
} from 'src/constants/routes'
import {
  useAiListeningDateRange,
  useAiListeningLink,
} from 'src/hooks/useAiListeningDateRange'
import { useQueryParams } from 'src/hooks/useQueryParams'
import { useGetAiListeningProfilePromptsOverviewQuery } from 'src/reactQuery/useGetAiListeningProfilePromptsOverviewQuery'
import { useGetAiListeningProfilePromptsQuery } from 'src/reactQuery/useGetAiListeningProfilePromptsQuery'
import { TabsSection } from 'src/pages/AIListening/Main/TabsSection'
import {
  ContextTagType,
  PromptStatus,
} from 'src/services/aiListeningApi/brandProfile.types'
import { FilterModal } from './FilterModal'
import { PerformanceHeader } from './PerformanceHeader'
import { AddPromptFlow } from '../shared/AddPrompt'
import { EntitySelect } from '../shared/EntitySelect'
import {
  rememberBrandProfile,
  useDebouncedValue,
  useResolvedBrandProfile,
} from '../shared/hooks'
import { AiListeningPage, PageBreadcrumb, PageTitleRow } from '../shared/Layout'
import { PromptList } from '../shared/PromptList'
import { StateCard } from '../shared/StateCard'
import { CONTEXT_TAG_TYPES } from '../shared/utils'

type PromptTab = PromptStatus | 'all'

interface PromptsQueryParams {
  context?: string
  profile?: string
  tab?: PromptTab
}

// TabsSection is shared with Social Listening and keeps its own tab names.
const TAB_TO_SECTION = {
  active: 'saved',
  all: 'all',
  completed: 'completed',
  draft: 'archived',
} as const

const SECTION_TO_TAB: Record<string, PromptTab> = {
  all: 'all',
  archived: 'draft',
  completed: 'completed',
  saved: 'active',
}

const SEARCH_DEBOUNCE_MS = 300

const parseContext = (value?: string): ContextTagType[] =>
  (value ?? '')
    .split(',')
    .filter((item): item is ContextTagType =>
      CONTEXT_TAG_TYPES.includes(item as ContextTagType)
    )

// /ai-listening/prompts?profile= — prompt performance for one brand profile,
// and its prompts by status.
export const PromptsPage: FC = () => {
  const navigate = useNavigate()
  const link = useAiListeningLink()
  const range = useAiListeningDateRange()
  const { queryParams, updateQueryParams } =
    useQueryParams<PromptsQueryParams>()
  const tab: PromptTab = queryParams.tab ?? 'active'
  const context = parseContext(queryParams.context)

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isAddingPrompt, setIsAddingPrompt] = useState(false)

  const {
    isLoading: isProfilesLoading,
    profile,
    profiles,
  } = useResolvedBrandProfile(queryParams.profile)
  const profileId = profile?._id

  useEffect(() => {
    if (profileId) rememberBrandProfile(profileId)
  }, [profileId])

  const { data: overview, isLoading: isOverviewLoading } =
    useGetAiListeningProfilePromptsOverviewQuery(profileId, range)
  const { data: promptData, isLoading: isPromptsLoading } =
    useGetAiListeningProfilePromptsQuery(profileId, {
      context,
      search: debouncedSearch,
      status: tab,
    })

  if (isProfilesLoading) return null

  if (!profile) {
    return (
      <AiListeningPage>
        <PageBreadcrumb
          fallbackTo={link(AI_LISTENING_BRAND_ROUTE)}
          items={['AI Listening', 'Prompts']}
        />
        <PageTitleRow title="Prompts" />
        <StateCard
          action={{
            label: 'Add Your Brand',
            onClick: () => navigate(link(AI_LISTENING_BRAND_NEW_ROUTE)),
          }}
          description="Prompts belong to a brand profile. Add your brand first, then the questions you want to track."
          icon="brand"
          title="No Brand Profile Added Yet"
        />
      </AiListeningPage>
    )
  }

  const counts = promptData?.counts
  const isFiltered = context.length > 0 || !!debouncedSearch

  return (
    <AiListeningPage>
      <PageBreadcrumb
        fallbackTo={link(AI_LISTENING_BRAND_PROFILE_ROUTE, {
          profileId: profile._id,
        })}
        items={['AI Listening', 'Prompts']}
      />
      <PageTitleRow title="Prompts">
        {profiles.length > 1 && (
          <EntitySelect
            activeId={profile._id}
            ariaLabel="Switch brand profile"
            onSelect={(id) => updateQueryParams({ profile: id })}
            options={profiles.map((item) => ({
              id: item._id,
              label: item.name,
            }))}
          />
        )}
      </PageTitleRow>

      <PerformanceHeader isLoading={isOverviewLoading} overview={overview} />

      <TabsSection
        activeTab={TAB_TO_SECTION[tab]}
        allCount={counts?.all ?? 0}
        archivedCount={counts?.draft ?? 0}
        completedCount={counts?.completed ?? 0}
        onFilterClick={() => setIsFilterOpen(true)}
        onSearchChange={setSearch}
        onTabChange={(section) =>
          updateQueryParams({ tab: SECTION_TO_TAB[section] })
        }
        savedCount={counts?.active ?? 0}
        searchValue={search}
      />

      <PromptList
        emptyState={
          isFiltered ? (
            <StateCard
              description="No prompts match your search or filters. Try other words or reset the filter."
              icon="prompt"
              inline
              title="No matching prompts"
            />
          ) : undefined
        }
        isLoading={isPromptsLoading}
        onAddPrompt={() => setIsAddingPrompt(true)}
        prompts={promptData?.prompts ?? []}
      />

      <FilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onSave={(types) => {
          updateQueryParams({
            context: types.length ? types.join(',') : undefined,
          })
          setIsFilterOpen(false)
        }}
        value={context}
      />

      <AddPromptFlow
        isOpen={isAddingPrompt}
        onClose={() => setIsAddingPrompt(false)}
        onViewProfile={() =>
          navigate(
            link(AI_LISTENING_BRAND_PROFILE_ROUTE, { profileId: profile._id })
          )
        }
        profile={profile}
      />
    </AiListeningPage>
  )
}
